<shared-ui-complex-table
   [cellTemplates]="cellTemplates()"
   [columns]="columnConfig()"
   [rows]="rows()"
   [subRows]="subRowsOf"
   [totalsTemplate]="totalsTemplate"
   class="text-sm"
></shared-ui-complex-table>

<ng-template
   #totalsTemplate
   let-columns
>
   <!-- Totals per kpi -->
   @for (total of totals()?.kpiData; track $index; let isFirstTotal = $first) {
      @for (col of columns; track $index) {
         @if (isFirstTotal || col.memberOfGroup !== 'order.product') {
            @let applyShading = col.field === 'kpi_investment' || col.field === 'targeting' || col.field === 'menu';
            <div
               [ngClass]="{
                  'text-right': col.type === 'number' || col.type === 'currency',
                  'background-shading': applyShading,
                  'pdf:bg-surface-container-highest': applyShading,
                  'rounded-bl-xl': $first && col.memberOfGroup === 'order.product',
               }"
               [style.grid-row]="col.memberOfGroup === 'order.product' ? 'span ' + (totals().kpiData.length + 1) : ''"
               class="bg-surface-dim font-bold"
            >
               @switch (col.field) {
                  @case ('id') {
                     <div>{{ 'general.total' | translate | uppercase }}</div>
                  }
                  @case ('invoice_price.value') {
                     <div>{{ orderInvestmentTotal() | currency: 'EUR' }}</div>
                  }
                  @case ('kpi.name') {
                     <div>{{ 'grid.kpi.' + total.kpislug | translate }}</div>
                  }
                  @case ('order_value') {
                     @if (orderKpiTotals()[total.kpislug] && !specialKpiSlugs.includes(total.kpislug)) {
                        <div>{{ orderKpiTotals()[total.kpislug].totalValue | number: '0.0-0' }}</div>
                     } @else {
                        <div class="-mx-2 background-shading h-full pdf:bg-surface-container-highest"></div>
                     }
                  }
                  @case ('intermediate_value') {
                     @if (orderKpiTotals()[total.kpislug] && !specialKpiSlugs.includes(total.kpislug)) {
                        <div>{{ orderKpiTotals()[total.kpislug].interimValue | number: '0.0-0' }}</div>
                     } @else {
                        <div class="-mx-2 background-shading h-full pdf:bg-surface-container-highest"></div>
                     }
                  }
                  @case ('kpi_value') {
                     @if (specialKpiSlugs.includes(total.kpislug)) {
                        @if (SPECIAL_KPI_VALUE_TYPES[total.kpislug] === 'number') {
                           <div>{{ total.value | number: '0.2-2' }}</div>
                        } @else {
                           <div>{{ total.value | percent: '0.2-2' }}</div>
                        }
                     } @else {
                        <div>{{ total.value | number: '0.0-0' }}</div>
                     }
                  }
                  @case ('rate') {
                     @if (rateHiddenBySlug()[total.kpislug]) {
                        <div class="-mx-2 background-shading h-full pdf:bg-surface-container-highest"></div>
                     } @else {
                        <div class="flex flex-auto gap-2 h-full">
                           <div class="text-outlineVariant text-xs pt-1">{{ total.rateTypeName }}</div>
                           <span class="ml-auto self-center">{{
                              total.rate | currency: 'EUR' : 'symbol' : '1.' + total.decimalPrecision + '-' + total.decimalPrecision
                           }}</span>
                        </div>
                     }
                  }
               }
            </div>
         }
      }
   }

   <!-- Bottom line with total investment (product group columns are already covered by the span above) -->
   @for (col of columns; track $index) {
      @if (col.memberOfGroup !== 'order.product') {
         @let applyShading = col.field !== 'kpi.name' && col.field !== 'kpi_investment';
         <div
            [ngClass]="{
               'background-shading': applyShading,
               'pdf:bg-surface-container-highest': applyShading,
               'rounded-br-xl': $last,
            }"
            class="bg-surface-dim font-bold"
         >
            @switch (col.field) {
               @case ('kpi.name') {
                  <div>{{ 'intermediate_report.investment' | translate }}</div>
               }
               @case ('kpi_investment') {
                  <div class="text-right">{{ totals()?.investment | currency: 'EUR' }}</div>
               }
            }
         </div>
      }
   }
</ng-template>

<!-- Grid cell templates -->
<ng-template
   #numberInput
   let-col="column"
   let-formGroup="subRow"
   let-index="subRowIndex"
>
   @let kpi = formGroup.value.kpi;
   @if (col.field === 'kpi_investment' && index > 0) {
      <!-- Only the primary KPI (which is always the first in the list) has to have an investment value entered -->
      <div class="-mx-2 background-shading h-full flex-auto pdf:bg-surface-container-highest"></div>
   } @else if (specialKpiSlugs.includes(kpi.slug)) {
      <!-- Even though we are mainly showing inputs there are some exceptions to that rule; some KPIs should not have a value entered and instead display a calculated percentage. These are the 'special' KPIs. -->
      @if (SPECIAL_KPI_VALUE_TYPES[kpi.slug] === 'number') {
         <div>{{ (formGroup | getFormControl: col.field)?.value | number: '0.2-2' }}</div>
      } @else {
         <div>{{ (formGroup | getFormControl: col.field)?.value | percent: '0.2-2' }}</div>
      }
   } @else {
      <!-- Just a regular KPI or investment with a normal input -->
      @let formControl = formGroup | getFormControl: col.field;
      @if (reportModeActive()) {
         <!-- Report/PDF export: render as plain text. Kendo drawDOM does not vertically center text inside <input>, which top-aligns values in tall rows. -->
         <div class="w-full text-right">
            @if (col.field === 'kpi_investment') {
               {{ formControl.value | currency: 'EUR' }}
            } @else {
               {{ formControl.value | number: '0.0-0' }}
            }
         </div>
      } @else {
         <div
            [ngClass]="formControl.disabled ? 'bg-surface-container' : 'bg-surface-container-lowest'"
            class="-mx-2 flex h-full flex-auto justify-end px-2 w-24"
         >
            <shared-ui-number-input
               [decimals]="col.field === 'kpi_investment' ? 2 : 0"
               [inputFormControl]="formControl"
               [label]="col.field | translate"
               [manualSet]="false"
               [materialInput]="false"
               [readonly]="false"
               [type]="col.field === 'kpi_investment' ? 'currency' : 'number'"
               class="h-full"
            ></shared-ui-number-input>
         </div>
      }
   }
</ng-template>

<ng-template
   #productCombined
   let-order="row"
>
   <div class="w-full min-w-56">
      <shared-ui-internal-order-product-display
         [order]="order"
         [showMargin]="false"
         [subtext]="reportModeActive() ? 'none' : 'product_group'"
      ></shared-ui-internal-order-product-display>
   </div>
</ng-template>

<ng-template
   #intermediateValue
   let-formGroup="subRow"
   let-order="row"
>
   @let kpi = formGroup.value.kpi;
   @if (formGroup.controls.orderValue?.value) {
      @if (specialKpiSlugs.includes(kpi.slug)) {
         <!-- The calculated special kpi percentage value does not change over time -->
         @if (SPECIAL_KPI_VALUE_TYPES[kpi.slug] === 'number') {
            <div>{{ formGroup.controls.orderValue.value | number: '1.2-2' }}</div>
         } @else {
            <div>{{ formGroup.controls.orderValue.value | percent: '1.2-2' }}</div>
         }
      } @else {
         <div>{{ localDataObject().get(order.id).interimValue | number: '0.0-0' }}</div>
      }
   } @else {
      <div class="-mx-2 background-shading flex-auto h-full pdf:bg-surface-container-highest"></div>
   }
</ng-template>

<ng-template
   #menu
   let-col="column"
   let-order="row"
>
   <div
      (click)="openKpiDialog(order)"
      class="m-0"
   >
      <button
         [disabled]="formHelperService.editingDisabled()"
         data-cy="intermediate-report-grid-kpi-button"
         mat-icon-button
      >
         <fa-icon [icon]="'list-check'"></fa-icon>
      </button>
   </div>
</ng-template>

<ng-template
   #rate
   let-col="column"
   let-formGroup="subRow"
>
   @let kpi = formGroup.value.kpi;
   <!-- Some kpis do not have a rate -->
   @if (rateHiddenBySlug()[kpi.slug]) {
      <div class="-mx-2 background-shading h-full flex-auto pdf:bg-surface-container-highest"></div>
   } @else {
      <div class="flex flex-auto gap-2 h-full">
         <div class="text-outlineVariant text-xs pt-1">{{ kpi.rate_type.name }}</div>
         <span class="ml-auto self-center">{{
            (formGroup | getFormControl: col.field)?.value
               | currency: 'EUR' : 'symbol' : '1.' + kpi.rate_type.decimal_precision + '-' + kpi.rate_type.decimal_precision
         }}</span>
      </div>
   }
</ng-template>

<ng-template
   #kpiName
   let-col="column"
   let-formGroup="subRow"
>
   @let kpi = formGroup.value.kpi;
   <div>{{ 'grid.kpi.' + kpi.slug | translate }}</div>
</ng-template>

<ng-template
   #orderValue
   let-formGroup="subRow"
>
   @let kpi = formGroup.value.kpi;
   @if (formGroup.controls.orderValue) {
      @if (specialKpiSlugs.includes(kpi.slug)) {
         @if (SPECIAL_KPI_VALUE_TYPES[kpi.slug] === 'number') {
            <div>{{ formGroup.controls.orderValue.value | number: '1.2-2' }}</div>
         } @else {
            <div>{{ formGroup.controls.orderValue.value | percent: '1.2-2' }}</div>
         }
      } @else {
         <div>{{ formGroup.controls.orderValue.value | number: '0.0-0' }}</div>
      }
   } @else {
      <div class="-mx-2 background-shading flex-auto h-full pdf:bg-surface-container-highest"></div>
   }
</ng-template>

<ng-template
   #specialist
   let-col="column"
   let-order="row"
>
   @let orderForm = formDataMap().get(order.id);
   <div class="flex items-center justify-center gap-1 h-full">
      @if (formHelperService.isReportStatusDone(orderForm.formGroup)) {
         <fa-icon
            [icon]="['fas', 'circle-check']"
            class="text-success"
            size="3x"
         ></fa-icon>
      } @else {
         <!-- A person or a team per discipline behind the order, shown the way the team cards show one -->
         @for (specialist of orderSpecialists().get(order.id); track specialist.type + ':' + specialist.id) {
            <shared-ui-avatar
               [subject]="specialist"
               [title]="specialist.name"
               size="sm"
            />
         }
      }
   </div>
</ng-template>

<ng-template
   #discipline
   let-index="subRowIndex"
   let-order="row"
>
   <fa-icon
      [icon]="order.products?.[0]?.main_category?.slug | disciplineIcon"
      class="pl-1 pr-2"
   ></fa-icon>

   <div>{{ 'categories.' + order.products?.[0]?.main_category?.slug | translate | ucfirst }}</div>
</ng-template>

<ng-template
   #targeting
   let-order="row"
>
   <div
      [innerHTML]="order.targeting | markdown"
      class="rendered-markdown"
   ></div>
</ng-template>
